import { DateTime } from 'luxon';
import dateFormatFilter from '@/filters/dateFormatFilter';

/**
 * Fiscal year is Apr 1 → Mar 31. Returns the calendar year of the Apr 1 that begins the fiscal year
 * containing `referenceDay` (local).
 * @param {import('luxon').DateTime} referenceDay
 */
function calendarYearOfFiscalYearAprilStart(referenceDay) {
  return referenceDay.month >= 4 ? referenceDay.year : referenceDay.year - 1;
}

/**
 * String stored in `filters.start_date` / `filters.end_date` (and URL query): app date format.
 * @param {import('luxon').DateTime} day
 */
function formatRevenueReportDateFilterString(day) {
  return dateFormatFilter(day.startOf('day').toISO());
}

/**
 * Default revenue report range: current fiscal year start through today (local).
 * @param {import('luxon').DateTime} [referenceDate] fixed “today” for tests
 * @returns {{ start_date: string, end_date: string }}
 */
export function revenueReportDefaultFiscalYearDates(referenceDate) {
  const today = (referenceDate || DateTime.local()).startOf('day');
  const aprilYear = calendarYearOfFiscalYearAprilStart(today);
  const fiscalYearStart = DateTime.local(aprilYear, 4, 1).startOf('day');
  return {
    start_date: formatRevenueReportDateFilterString(fiscalYearStart),
    end_date: formatRevenueReportDateFilterString(today),
  };
}

/**
 * Full prior fiscal year (Apr 1 → Mar 31), local.
 * @param {import('luxon').DateTime} [referenceDate]
 * @returns {{ start_date: string, end_date: string }}
 */
export function revenueReportLastFiscalYearDateRange(referenceDate) {
  const today = (referenceDate || DateTime.local()).startOf('day');
  const aprilYear = calendarYearOfFiscalYearAprilStart(today);
  const start = DateTime.local(aprilYear - 1, 4, 1).startOf('day');
  return {
    start_date: formatRevenueReportDateFilterString(start),
    end_date: formatRevenueReportDateFilterString(today),
  };
}

/**
 * Calendar year to date: Jan 1 through today (local).
 * @param {import('luxon').DateTime} [referenceDate]
 * @returns {{ start_date: string, end_date: string }}
 */
export function revenueReportCalendarYearToDateRange(referenceDate) {
  const today = (referenceDate || DateTime.local()).startOf('day');
  const jan1 = DateTime.local(today.year, 1, 1).startOf('day');
  return {
    start_date: formatRevenueReportDateFilterString(jan1),
    end_date: formatRevenueReportDateFilterString(today),
  };
}

/**
 * Parse a route query value into the same date string shape as filter fields (ISO or browser-parsable).
 * @param {string|null|undefined} rawQueryValue
 * @returns {string|null}
 */
export function normalizeRevenueReportDateQueryParam(rawQueryValue) {
  if (rawQueryValue == null || rawQueryValue === '') {
    return null;
  }
  const s = String(rawQueryValue).trim();
  let parsed = DateTime.fromISO(s);
  if (!parsed.isValid) {
    parsed = DateTime.fromJSDate(new Date(s));
  }
  return parsed.isValid ? formatRevenueReportDateFilterString(parsed) : null;
}

/**
 * Turn a stored filter date string into a JS Date for the range picker (avoids flatpickr parsing quirks).
 * @param {string|null|undefined} filterDateString
 * @returns {Date|null}
 */
export function revenueReportDateFilterStringToJsDate(filterDateString) {
  if (filterDateString == null || filterDateString === '') {
    return null;
  }
  const s = String(filterDateString).trim();
  let parsed = DateTime.fromISO(s);
  if (!parsed.isValid) {
    parsed = DateTime.fromJSDate(new Date(s));
  }
  return parsed.isValid ? parsed.startOf('day').toJSDate() : null;
}
